<template>
  <div class="chart">
    <Empty
      v-show="isEmpty"
      class="chart-empty"
      tips="当前数据为空"
    />
    <div
      ref="chart"
      style="height: 500px"
    />
  </div>
</template>

<script>
import echarts from 'echarts';
import { uniq } from 'lodash';
import accounting from 'accounting';

import Empty from '@/components/Empty';

import { isEmpty } from '@/common/util';
import { getField } from '@/config/columns/index';
import { getMaxIntFromNumbers } from '@/common/chart';
import defaultLineOptions from '@/config/chart/line';
import defaultPieOptions from '@/config/chart/pie';

export default {
  components: {
    Empty,
  },

  props: {
    dataSource: {
      type: Object,
      required: true,
      default() {
        return {};
      },
    },

    type: {
      type: String,
      default: '',
    },

    field: {
      type: Array,
      default: () => [],
    },

    dimension: {
      type: String,
      default: '',
    },

    proType: {
      type: String,
      default: 'pie',
    },
  },

  computed: {
    base() {
      return isEmpty(this.dataSource.base);
    },

    baseDate() {
      if (!this.base) return null;
      return isEmpty(this.base.date);
    },

    baseList() {
      if (!this.base) return null;
      return isEmpty(this.base.list);
    },

    compare() {
      return isEmpty(this.dataSource.compare);
    },

    compareDate() {
      if (!this.compare) return null;
      return isEmpty(this.compare.date);
    },

    compareList() {
      if (!this.compare) return null;
      return isEmpty(this.compare.list);
    },

    isEmpty() {
      if (this.type === 'trend') {
        return !(this.baseDate && this.baseList);
      } else if (this.type === 'proportion') {
        if (this.proType === 'pie') {
          return !this.base;
        } else if (this.proType === 'line') {
          return this.dataSource.length === 0;
        }
      }
      return false;
    },
  },

  watch: {
    dataSource() {
      this.render();
    },
  },

  mounted() {
    this.chart = echarts.init(this.$refs['chart']);
    window.addEventListener('resize', this.resize, false);
  },

  destroyed() {
    window.removeEventListener('resize', this.resize, false);
  },

  methods: {
    render() {
      if (this.type === 'trend') {
        if (this.baseDate && this.baseList) {
          const options = this.renderTrend();
          const opt = Object.assign(defaultLineOptions, options);
          opt.dataZoom = [];
          this.chart.setOption(opt, true);
        }
      } else if (this.type === 'proportion') {
        if (this.proType === 'pie') {
          if (this.base) {
            const options = this.renderProportion();
            const opt = Object.assign(defaultPieOptions, options);
            this.chart.setOption(opt, true);
          }
        } else if (this.proType === 'line') {
          const options = this.renderProportionLine();
          const opt = Object.assign(defaultLineOptions, options);
          console.dir(opt);
          this.chart.setOption(opt, true);
        }
      }
    },

    renderProportionLine() {
      const { date, labels, list } = this.dataSource;
      const max = [];

      const option = {
        legend: {
          type: 'plain',
          left: 'left',
          top: 8,
          itemWidth: 8,
          itemHeight: 8,
          icon: 'rect',
          data: labels,
        },

        xAxis: {
          type: 'category',
          data: date,
        },

        series: [],
      };

      list.forEach((item, index) => {
        max.push(getMaxIntFromNumbers(item));
        option.series.push({
          type: 'line',
          name: labels[index],
          data: item,
        });
      });

      option.yAxis = {
        name: getField(this.field[0]).name,
        type: 'value',
        min: 0,
        max: getMaxIntFromNumbers(max),
        splitNumber: 5,
        interval: (getMaxIntFromNumbers(max) - 0) / 5,
        axisLabel: {
          formatter: '{value}',
        },
      };

      return option;
    },

    renderTrend() {
      const options = {
        series: [],
        xAxis: [],
        yAxis: [],
      };

      if (this.base) {
        if (this.baseDate) {
          options.xAxis.push({
            type: 'category',
            data: this.baseDate,
          });
        }

        if (this.baseList) {
          Object.keys(this.baseList).forEach((key, index) => {
            const data = this.baseList[key];

            options.series.push({
              type: 'line',
              name: getField(this.field[index]).name,
              data,
              yAxisIndex: index,
            });

            options.yAxis.push({
              type: 'value',
              min: 0,
              max: getMaxIntFromNumbers(data),
              splitNumber: 5,
              interval: (getMaxIntFromNumbers(data) - 0) / 5,
              axisLabel: {
                formatter: '{value}',
              },
            });
          });
        }
      }

      if (this.compare) {
        if (this.compareDate) {
          options.xAxis.push({
            type: 'category',
            data: this.compareDate,
            position: 'bottom',
            offset: 30,
            axisTick: {
              show: false,
            },
            axisLine: {
              show: false,
            },
          });
        }

        if (this.compareList) {
          Object.keys(this.compareList).forEach((key, index) => {
            const data = this.compareList[key];

            options.series.push({
              type: 'line',
              name: `${this.compareDate[0]} ~ ${
                this.compareDate[this.compareDate.length - 1]
              }`,
              data,
            });

            const max = getMaxIntFromNumbers(data);
            options.yAxis[0].max = Math.max(options.yAxis[0].max, max);
            options.series[0].name = `${this.baseDate[0]} ~ ${
              this.baseDate[this.baseDate.length - 1]
            }`;
          });
        }
      }

      return options;
    },

    renderProportion() {
      const options = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b} : {c} ({d}%)',
        },

        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 10,
          top: 20,
          bottom: 20,
          data: [],
        },

        series: [],
      };

      // 维度
      // const dimension = this.dimension;
      // 指标
      const field = this.field[0];
      const legendData = [];

      if (this.base) {
        // 数据
        const data = [];
        // 总数
        let total = 0;

        this.base.forEach((item) => {
          legendData.push(item.name);
          data.push({
            name: item.name,
            value: item[field],
          });
          total += parseFloat(item[field]);
        });

        options.series.push({
          name: getField(field).name,
          type: 'pie',
          radius: ['50%', '70%'],
          center: ['40%', '50%'],
          data,
          avoidLabelOverlap: false,
          label: {
            normal: {
              show: true,
              position: 'center',
              textStyle: {
                fontSize: '20',
                fontWeight: 'bold',
              },
              formatter() {
                const isMoney = field.indexOf('cost') !== -1;
                return `${getField(field).name} ${
                  isMoney
                    ? accounting.formatMoney(total, '￥')
                    : accounting.formatNumber(total)
                }`;
              },
            },
            emphasis: {
              show: true,
              textStyle: {
                fontSize: '20',
                fontWeight: 'bold',
              },
            },
          },
          labelLine: {
            normal: {
              show: false,
            },
          },
        });
      }

      if (this.compare) {
        // 数据
        const data = [];
        // 总数
        let total = 0;
        this.compare.forEach((item) => {
          legendData.push(item.name);
          data.push({
            name: item.name,
            value: item[field],
          });
          total += parseInt(item[field], 10);
        });

        options.series.push({
          name: getField(field).name,
          type: 'pie',
          radius: ['30%', '50%'],
          center: ['60%', '50%'],
          data,
          avoidLabelOverlap: false,
          label: {
            normal: {
              show: true,
              position: 'center',
              textStyle: {
                fontSize: '14',
                fontWeight: 'bold',
              },
              formatter() {
                const isMoney = field.indexOf('cost') !== -1;
                return `${getField(field).name} ${
                  isMoney
                    ? accounting.formatMoney(total, '￥')
                    : accounting.formatNumber(total)
                }`;
              },
            },
            emphasis: {
              show: true,
              textStyle: {
                fontSize: '14',
                fontWeight: 'bold',
              },
            },
          },
          labelLine: {
            normal: {
              show: false,
            },
          },
        });

        if (options.series[0]) {
          options.series[0].radius = ['30%', '50%'];
          options.series[0].center = ['20%', '50%'];
          options.series[0].label.normal.textStyle.fontSize = '14';
          options.series[0].label.emphasis.textStyle.fontSize = '14';
        }

        this.compare.forEach((item) => {
          legendData.push(item.name);
        });
      }

      options.legend.data = uniq(legendData);

      return options;
    },
  },
};
</script>

<style lang="less" scoped>
.chart {
  position: relative;

  &-empty {
    width: 100%;
    position: absolute;
    height: 400px;
    z-index: 10;
    background: #fff;
  }
}
</style>